import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Grid,
  Button,
  FormControlLabel,
  Switch,
} from "@mui/material";
import { MicroPractice } from "../../store/slices/types";

interface MicroPracticeFormProps {
  microPractice?: MicroPractice | null;
  onSave: (microPracticeData: Partial<MicroPractice>) => void;
  onCancel: () => void;
  saving?: boolean;
}

const MicroPracticeForm: React.FC<MicroPracticeFormProps> = ({
  microPractice,
  onSave,
  onCancel,
  saving = false,
}) => {
  const [formData, setFormData] = useState({
    title: microPractice?.title || "",
    description: microPractice?.description || "",
    duration: microPractice?.duration || 0,
    category: microPractice?.category || "Breathe & Reset",
    difficulty: microPractice?.difficulty || "beginner",
    color: microPractice?.color || "#667eea",
    tags: microPractice?.tags?.join(", ") || "",
    isActive:
      microPractice?.isActive !== undefined ? microPractice.isActive : true,
    instructions: microPractice?.instructions?.join("\n") || "",
    benefits: microPractice?.benefits?.join("\n") || "",
  });

  useEffect(() => {
    setFormData({
      title: microPractice?.title || "",
      description: microPractice?.description || "",
      duration: microPractice?.duration || 0,
      category: microPractice?.category || "Breathe & Reset",
      difficulty: microPractice?.difficulty || "beginner",
      color: microPractice?.color || "#667eea",
      tags: microPractice?.tags?.join(", ") || "",
      isActive:
        microPractice?.isActive !== undefined ? microPractice.isActive : true,
      instructions: microPractice?.instructions?.join("\n") || "",
      benefits: microPractice?.benefits?.join("\n") || "",
    });
    setErrors({});
  }, [microPractice]);

  const [errors, setErrors] = useState<{ [key: string]: string }>({});

  const validateForm = () => {
    const newErrors: { [key: string]: string } = {};

    if (!formData.title.trim()) {
      newErrors.title = "Practice title is required";
    } else if (formData.title.trim().length < 3) {
      newErrors.title = "Practice title must be at least 3 characters";
    }

    if (!formData.description.trim()) {
      newErrors.description = "Description is required";
    } else if (formData.description.trim().length < 10) {
      newErrors.description = "Description must be at least 10 characters";
    }

    if (formData.duration <= 0) {
      newErrors.duration = "Duration must be greater than 0";
    } else if (formData.duration > 60) {
      newErrors.duration = "Duration cannot exceed 60 minutes";
    }

    if (!formData.instructions.trim()) {
      newErrors.instructions = "Instructions are required";
    }

    if (!formData.benefits.trim()) {
      newErrors.benefits = "Benefits are required";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = () => {
    if (validateForm()) {
      const microPracticeData = {
        ...formData,
        tags: formData.tags
          .split(",")
          .map((tag) => tag.trim())
          .filter((tag) => tag),
        instructions: formData.instructions
          .split("\n")
          .map((instruction) => instruction.trim())
          .filter((instruction) => instruction),
        benefits: formData.benefits
          .split("\n")
          .map((benefit) => benefit.trim())
          .filter((benefit) => benefit),
      };
      onSave(microPracticeData);
    }
  };

  return (
    <Box>
      {/* Basic Information Section */}
      <Box sx={{ mb: 4 }}>
        <Typography
          variant='h6'
          fontWeight='600'
          gutterBottom
          sx={{
            color: "primary.main",
            display: "flex",
            alignItems: "center",
            gap: 1,
          }}
        >
          📝 Basic Information
        </Typography>

        <Grid container spacing={3}>
          <Grid item xs={12}>
            <TextField
              fullWidth
              label='Practice Title'
              placeholder='Enter a descriptive title for your practice'
              value={formData.title}
              onChange={(e) => {
                setFormData({ ...formData, title: e.target.value });
                if (errors.title) setErrors({ ...errors, title: "" });
              }}
              error={!!errors.title}
              helperText={errors.title}
              sx={{
                "& .MuiOutlinedInput-root": {
                  borderRadius: 1.5,
                  minHeight: 64,
                },
              }}
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              fullWidth
              multiline
              rows={3}
              label='Description'
              placeholder='Provide a detailed description of this practice'
              value={formData.description}
              onChange={(e) => {
                setFormData({ ...formData, description: e.target.value });
                if (errors.description)
                  setErrors({ ...errors, description: "" });
              }}
              error={!!errors.description}
              helperText={errors.description}
              sx={{
                "& .MuiOutlinedInput-root": {
                  borderRadius: 1.5,
                  minHeight: 100,
                },
              }}
            />
          </Grid>
        </Grid>
      </Box>

      {/* Practice Configuration Section */}
      <Box sx={{ mb: 4 }}>
        <Typography
          variant='h6'
          fontWeight='600'
          gutterBottom
          sx={{
            color: "primary.main",
            display: "flex",
            alignItems: "center",
            gap: 1,
          }}
        >
          ⚙️ Practice Configuration
        </Typography>

        <Grid container spacing={3}>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              type='number'
              label='Duration (minutes)'
              placeholder='e.g., 5'
              value={formData.duration || ""}
              onChange={(e) => {
                const value = parseInt(e.target.value) || 0;
                setFormData({ ...formData, duration: value });
                if (errors.duration) setErrors({ ...errors, duration: "" });
              }}
              error={!!errors.duration}
              helperText={
                errors.duration ||
                "How long will this practice take to complete?"
              }
              inputProps={{
                min: 1,
                max: 60,
                step: 1,
              }}
              InputProps={{
                startAdornment: (
                  <Typography sx={{ mr: 1, color: "text.secondary" }}>
                    ⏱️
                  </Typography>
                ),
              }}
              sx={{
                "& .MuiOutlinedInput-root": {
                  borderRadius: 1.5,
                  minHeight: 64,
                },
              }}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <FormControl fullWidth>
              <InputLabel>Category</InputLabel>
              <Select
                label='Category'
                value={formData.category}
                onChange={(e) =>
                  setFormData({
                    ...formData,
                    category: e.target.value as any,
                  })
                }
                sx={{
                  borderRadius: 1.5,
                  minHeight: 64,
                }}
              >
                <MenuItem value='Breathe & Reset'>
                  🌬️ Breathe & Reset
                </MenuItem>
                <MenuItem value='Reframe & Focus'>
                  🧘‍♀️ Reframe & Focus
                </MenuItem>
                <MenuItem value='Gratitude & Mindfulness'>
                  🙏 Gratitude & Mindfulness
                </MenuItem>
                <MenuItem value='Reflect & Recover'>
                  🤔 Reflect & Recover
                </MenuItem>
                <MenuItem value='Movement'>🏃‍♀️ Movement</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={6}>
            <FormControl fullWidth>
              <InputLabel>Difficulty Level</InputLabel>
              <Select
                label='Difficulty Level'
                value={formData.difficulty}
                onChange={(e) =>
                  setFormData({
                    ...formData,
                    difficulty: e.target.value as any,
                  })
                }
                sx={{
                  borderRadius: 1.5,
                  minHeight: 64,
                }}
              >
                <MenuItem value='beginner'>🟢 Beginner</MenuItem>
                <MenuItem value='intermediate'>🟡 Intermediate</MenuItem>
                <MenuItem value='advanced'>🔴 Advanced</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label='Color'
              placeholder='#667eea'
              value={formData.color}
              onChange={(e) =>
                setFormData({ ...formData, color: e.target.value })
              }
              helperText='Hex color code for the practice theme'
              InputProps={{
                startAdornment: (
                  <Typography sx={{ mr: 1, color: "text.secondary" }}>
                    🎨
                  </Typography>
                ),
              }}
              sx={{
                "& .MuiOutlinedInput-root": {
                  borderRadius: 1.5,
                  minHeight: 64,
                },
              }}
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              fullWidth
              label='Tags'
              placeholder='breathing, calm, stress-relief'
              value={formData.tags}
              onChange={(e) =>
                setFormData({ ...formData, tags: e.target.value })
              }
              helperText='Separate tags with commas to help users find this practice'
              InputProps={{
                startAdornment: (
                  <Typography sx={{ mr: 1, color: "text.secondary" }}>
                    🏷️
                  </Typography>
                ),
              }}
              sx={{
                "& .MuiOutlinedInput-root": {
                  borderRadius: 1.5,
                  minHeight: 64,
                },
              }}
            />
          </Grid>
        </Grid>
      </Box>

      {/* Instructions and Benefits Section */}
      <Box sx={{ mb: 4 }}>
        <Typography
          variant='h6'
          fontWeight='600'
          gutterBottom
          sx={{
            color: "primary.main",
            display: "flex",
            alignItems: "center",
            gap: 1,
          }}
        >
          📋 Instructions & Benefits
        </Typography>

        <Grid container spacing={3}>
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              multiline
              rows={6}
              label='Instructions'
              placeholder='Enter step-by-step instructions (one per line)'
              value={formData.instructions}
              onChange={(e) => {
                setFormData({ ...formData, instructions: e.target.value });
                if (errors.instructions)
                  setErrors({ ...errors, instructions: "" });
              }}
              error={!!errors.instructions}
              helperText={
                errors.instructions || "Enter each instruction on a new line"
              }
              sx={{
                "& .MuiOutlinedInput-root": {
                  borderRadius: 1.5,
                  minHeight: 150,
                },
              }}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              multiline
              rows={6}
              label='Benefits'
              placeholder='Enter the benefits of this practice (one per line)'
              value={formData.benefits}
              onChange={(e) => {
                setFormData({ ...formData, benefits: e.target.value });
                if (errors.benefits) setErrors({ ...errors, benefits: "" });
              }}
              error={!!errors.benefits}
              helperText={errors.benefits || "Enter each benefit on a new line"}
              sx={{
                "& .MuiOutlinedInput-root": {
                  borderRadius: 1.5,
                  minHeight: 150,
                },
              }}
            />
          </Grid>
        </Grid>
      </Box>

      {/* Status Section */}
      <Box sx={{ mb: 4 }}>
        <Grid container spacing={3}>
          <Grid item xs={12} sm={6}>
            <FormControlLabel
              control={
                <Switch
                  checked={formData.isActive}
                  onChange={(e) =>
                    setFormData({ ...formData, isActive: e.target.checked })
                  }
                />
              }
              label='Active'
            />
          </Grid>
        </Grid>
      </Box>

      {/* Action Buttons */}
      <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 2, mt: 4 }}>
        <Button
          onClick={onCancel}
          variant='outlined'
          sx={{
            borderRadius: 2,
            px: 3,
            py: 1.5,
            textTransform: "none",
            fontWeight: 500,
          }}
        >
          Cancel
        </Button>
        <Button
          onClick={handleSubmit}
          variant='contained'
          disabled={saving}
          sx={{
            borderRadius: 2,
            px: 3,
            py: 1.5,
            textTransform: "none",
            fontWeight: 600,
            background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
            "&:hover": {
              background: "linear-gradient(135deg, #5a67d8 0%, #6b46c1 100%)",
            },
          }}
        >
          {saving
            ? "Saving..."
            : microPractice
            ? "Update Practice"
            : "Create Practice"}
        </Button>
      </Box>
    </Box>
  );
};

export default MicroPracticeForm;

